import { useCallback, useEffect, useRef, useState } from "react";

// A drag-down-to-refresh gesture for the main scrollable content pane.
// Only ever hijacks the touch gesture when the container is already
// scrolled to the very top and the drag is downward — anywhere else it's a
// no-op, so normal scrolling elsewhere on the page is untouched.
const TRIGGER_PX = 64; // how far down counts as "far enough to refresh"
const MAX_PULL_PX = 96; // visual cap so the indicator doesn't chase the finger forever
const DRAG_RESISTANCE = 0.5;

export function usePullToRefresh<T extends HTMLElement>(onRefresh: () => void | Promise<unknown>) {
  // A callback ref, not useRef: some callers (e.g. TenantApp, which stays on
  // a plain spinner until an async onboarding check resolves) don't mount
  // the ref'd element on their very first render. A useRef-based ref stays
  // null through that render with no way to know it later became non-null,
  // so an effect with a `[]` dependency array that reads ref.current once
  // would permanently miss it. Storing the node in state instead means React
  // calls this callback — and the attach effect below re-runs — the moment
  // the element actually mounts, however late that is.
  const [el, setEl] = useState<T | null>(null);
  const ref = useCallback((node: T | null) => setEl(node), []);

  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const refreshingRef = useRef(false);

  useEffect(() => {
    if (!el) return;

    let startY: number | null = null;
    let dragging = false;

    function onTouchStart(e: TouchEvent) {
      if (refreshingRef.current || el!.scrollTop > 0) return;
      startY = e.touches[0].clientY;
      dragging = true;
    }

    function onTouchMove(e: TouchEvent) {
      if (!dragging || startY === null) return;
      const dy = e.touches[0].clientY - startY;
      if (dy <= 0 || el!.scrollTop > 0) {
        dragging = false;
        setPull(0);
        return;
      }
      e.preventDefault();
      setPull(Math.min(MAX_PULL_PX, dy * DRAG_RESISTANCE));
    }

    function onTouchEnd() {
      if (!dragging) return;
      dragging = false;
      startY = null;
      setPull((p) => {
        if (p >= TRIGGER_PX && !refreshingRef.current) {
          refreshingRef.current = true;
          setRefreshing(true);
          Promise.resolve(onRefreshRef.current()).finally(() => {
            refreshingRef.current = false;
            setRefreshing(false);
          });
        }
        return 0;
      });
    }

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [el]);

  return { ref, pull, refreshing, triggerPx: TRIGGER_PX };
}
